import React, { useState, useEffect } from "react";
import { apiClient } from "../services/authService";
import "./ExamenInteractivo.css";
import "./ExamenScrollView.css";
import TextoConImagenes from "./TextoConImagenes";
import ResultadosDetallados from "./ResultadosDetallados";
import MetricasCategorias from "./MetricasCategorias";
import CaseStudyContent from "./CaseStudyContent";
import { recordExamAttempt } from "./availableExams/learningStatsStorage";
import PreguntaInteractiva, {
  lookupRespuestaCorrecta,
} from "./PreguntaInteractiva";

const ProfileIcon = () => (
  <div
    style={{
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      background: "#fff",
      color: "#0c4a6e",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
    }}
  >
    U
  </div>
);

function normalizeRespuestas(resp, examenData) {
  const r = resp ?? {};
  if (!examenData?.preguntas) return r;
  const next = { ...r };
  examenData.preguntas.forEach((p) => {
    const val = next[p.id];
    if (Array.isArray(val) && val.length === 1 && typeof val[0] === "object" && val[0] !== null) {
      const first = val[0];
      const looksLikeHotspotOrDragDrop = Object.keys(first).some(
        (k) => k.startsWith("dropdown_") || k.startsWith("drop_zone_")
      );
      if (looksLikeHotspotOrDragDrop) {
        next[p.id] = first;
      }
    }
    const isMultiChoiceMultiCorrect =
      p.tipo_pregunta === "multiple_choice" &&
      (p.opciones?.filter((o) => o.correcta).length ?? 0) > 1;
    if (
      isMultiChoiceMultiCorrect &&
      next[p.id] !== undefined &&
      next[p.id] !== null &&
      !Array.isArray(next[p.id])
    ) {
      next[p.id] = [next[p.id]];
    }
  });
  return next;
}

function trainingRevealAvailableFor(pregunta, respuestasCorrectas) {
  if (!pregunta) return false;
  if (Object.keys(respuestasCorrectas).length > 0) {
    if (lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id) != null) return true;
  }
  const tipoPrincipal =
    pregunta.tipo_pregunta === "case_study"
      ? pregunta.case_study_data?.pregunta_principal_tipo || "multiple_choice"
      : pregunta.tipo_pregunta;
  const esMultipleChoice =
    !tipoPrincipal || tipoPrincipal === "multiple_choice" || tipoPrincipal === "";
  if (esMultipleChoice) {
    return (pregunta.opciones || []).some((o) => o.correcta);
  }
  return false;
}

function isPreguntaRespondida(respuesta) {
  if (respuesta == null || respuesta === "") return false;
  if (Array.isArray(respuesta)) return respuesta.length > 0;
  if (typeof respuesta === "object") return Object.keys(respuesta).length > 0;
  return true;
}

/**
 * Vista alternativa de toma de examen: todas las preguntas en scroll
 * (estilo preview de Create Exam). Se activa con change_default_view = "yes".
 */
function ExamenScrollView({ sesionData, onVolver }) {
  const { sesion_id, examen, estado_guardado, modoExamen: modoExamenProp } = sesionData;
  const modoExamen = modoExamenProp ?? "real";

  const [respuestas, setRespuestas] = useState(() =>
    normalizeRespuestas(estado_guardado?.respuestas ?? {}, examen)
  );
  const [marcadas, setMarcadas] = useState(estado_guardado?.preguntas_marcadas ?? {});
  const [reveladas, setReveladas] = useState({});
  const [finalizado, setFinalizado] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmFinish, setShowConfirmFinish] = useState(false);
  const [mobileReviewOpen, setMobileReviewOpen] = useState(false);
  const [respuestasCorrectas, setRespuestasCorrectas] = useState({});

  const duracionMinutos = examen?.duracion_minutos ?? 120;
  const [tiempoRestante, setTiempoRestante] = useState(duracionMinutos * 60);
  const [timerVisible, setTimerVisible] = useState(true);
  const [timerFinalizado, setTimerFinalizado] = useState(false);

  const preguntas = examen?.preguntas || [];
  const totalPreguntas = preguntas.length;
  const preguntasOrden = preguntas.map((p) => p.id);

  useEffect(() => {
    if (modoExamen !== "training" || !sesion_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.post("/examen/respuestas-correctas", { sesion_id });
        if (!cancelled) setRespuestasCorrectas(res.data?.respuestas_correctas || {});
      } catch {
        if (!cancelled) setRespuestasCorrectas({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoExamen, sesion_id]);

  useEffect(() => {
    if (modoExamen !== "real" || finalizado || timerFinalizado) return;
    const interval = setInterval(() => {
      setTiempoRestante((prev) => {
        if (prev <= 1) {
          setTimerFinalizado(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [modoExamen, finalizado, timerFinalizado]);

  useEffect(() => {
    if (!timerFinalizado || finalizado) return;
    handleFinalizarExamen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerFinalizado]);

  const formatTiempo = (segundos) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${String(horas).padStart(2, "0")} : ${String(minutos).padStart(2, "0")} : ${String(segs).padStart(2, "0")}`;
  };

  const handleSelectOpcion = (idPregunta, idOpcion) => {
    if (modoExamen === "training" && reveladas[idPregunta]) return;
    const preg = preguntas.find((p) => p.id === idPregunta);
    const isMultiSelect = (preg?.opciones?.filter((o) => o.correcta).length ?? 0) > 1;

    if (isMultiSelect) {
      setRespuestas((prev) => {
        const current = prev[idPregunta];
        const arr = Array.isArray(current) ? [...current] : current != null ? [current] : [];
        const newArr = arr.includes(idOpcion)
          ? arr.filter((x) => x !== idOpcion)
          : [...arr, idOpcion];
        return { ...prev, [idPregunta]: newArr };
      });
    } else {
      setRespuestas((prev) => ({ ...prev, [idPregunta]: idOpcion }));
    }
  };

  const handleSelectHotspotOpcion = (idPregunta, dropdownId, idOpcion) => {
    if (modoExamen === "training" && reveladas[idPregunta]) return;
    setRespuestas((prev) => {
      const respuestaActual = prev[idPregunta];
      const respuestaHotspot =
        typeof respuestaActual === "object" && respuestaActual !== null
          ? { ...respuestaActual }
          : {};
      respuestaHotspot[`dropdown_${dropdownId}`] = idOpcion;
      return { ...prev, [idPregunta]: respuestaHotspot };
    });
  };

  const handleSelectDragDropZone = (idPregunta, dropZoneId, draggableItemId) => {
    if (modoExamen === "training" && reveladas[idPregunta]) return;
    setRespuestas((prev) => {
      const respuestaActual = prev[idPregunta];
      const respuestaDragDrop =
        typeof respuestaActual === "object" && respuestaActual !== null
          ? { ...respuestaActual }
          : {};
      if (draggableItemId === null) {
        delete respuestaDragDrop[`drop_zone_${dropZoneId}`];
      } else {
        respuestaDragDrop[`drop_zone_${dropZoneId}`] = draggableItemId;
      }
      return { ...prev, [idPregunta]: respuestaDragDrop };
    });
  };

  const toggleMarkReview = (idPregunta) => {
    setMarcadas((prev) => ({ ...prev, [idPregunta]: !prev[idPregunta] }));
  };

  const toggleReveal = (idPregunta) => {
    setReveladas((prev) => ({ ...prev, [idPregunta]: !prev[idPregunta] }));
  };

  const guardarEstado = async (preguntaIdx = 0) => {
    await apiClient.post("/sesion/guardar", {
      sesion_id,
      pregunta_actual_idx: preguntaIdx,
      respuestas,
      marcadas,
    });
  };

  const handleSaveAndEndSession = async () => {
    setIsSaving(true);
    try {
      await guardarEstado(0);
      onVolver();
    } catch (error) {
      console.error("Error al guardar la sesión:", error);
      alert("Could not save the session.");
      setIsSaving(false);
    }
  };

  const handleEndExam = () => {
    setShowConfirmFinish(true);
  };

  const irAPregunta = (indexPregunta) => {
    setMobileReviewOpen(false);
    const el = document.getElementById(`scroll-q-${indexPregunta}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderReviewGrid = () => (
    <>
      <div className="examen-scroll-review-grid">
        {preguntas.map((p, index) => {
          const r = respuestas[p.id] ?? respuestas[String(p.id)];
          const answered = isPreguntaRespondida(r);
          const marked = !!marcadas[p.id];
          const cellClass = [
            "examen-scroll-review-cell",
            marked
              ? "examen-scroll-review-cell--marked"
              : answered
              ? "examen-scroll-review-cell--answered"
              : "examen-scroll-review-cell--pending",
          ].join(" ");

          return (
            <button
              key={p.id}
              type="button"
              className={cellClass}
              onClick={() => irAPregunta(index)}
              title={
                marked
                  ? `Question ${index + 1} — marked for review`
                  : answered
                  ? `Question ${index + 1} — answered`
                  : `Question ${index + 1} — not answered`
              }
            >
              {index + 1}
            </button>
          );
        })}
      </div>
      <div className="examen-scroll-review-legend">
        <span><i className="examen-scroll-review-dot examen-scroll-review-dot--answered" /> Answered</span>
        <span><i className="examen-scroll-review-dot examen-scroll-review-dot--pending" /> Pending</span>
        <span><i className="examen-scroll-review-dot examen-scroll-review-dot--marked" /> Marked</span>
      </div>
    </>
  );

  const handleFinalizarExamen = async (skipConfirm = false) => {
    if (!skipConfirm) {
      setShowConfirmFinish(true);
      return;
    }

    setShowConfirmFinish(false);
    setIsSaving(true);
    try {
      await guardarEstado(0);
      const res = await apiClient.post("/examen/calificar", { sesion_id });
      try {
        const resCorrectas = await apiClient.post("/examen/respuestas-correctas", {
          sesion_id,
        });
        setRespuestasCorrectas(resCorrectas.data.respuestas_correctas || {});
      } catch {
        setRespuestasCorrectas({});
      }
      setResultado(res.data);
      setFinalizado(true);
      try {
        const r = res.data;
        const total = Number(r.total_preguntas) || 0;
        const brut = Number(r.puntuacion_bruta) || 0;
        const pct = total > 0 ? Math.round((brut / total) * 100) : 0;
        if (examen?.id != null) recordExamAttempt(examen.id, pct);
      } catch {
        /* ignore */
      }
    } catch (error) {
      console.error("Error al finalizar el examen:", error);
      alert("An error occurred while grading. Please contact the administrator.");
      setIsSaving(false);
    }
  };

  if (!examen) {
    return (
      <div className="examen-layout">
        <header className="examen-main-header">
          <img src="/logos/examcat_logo.png" alt="Examcat" className="brand-logo" />
          <ProfileIcon />
        </header>
        <main className="examen-scrollable-content" style={{ padding: 40, textAlign: "center" }}>
          <p>Loading exam...</p>
        </main>
      </div>
    );
  }

  if (finalizado) {
    return (
      <div className="examen-layout examen-resultados-layout">
        <header className="examen-main-header">
          <img src="/logos/examcat_logo.png" alt="Examcat" className="brand-logo" />
          <ProfileIcon />
        </header>
        <main className="examen-scrollable-content examen-resultados-content">
          <div className="examen-resultados-inner">
            <h2 className="examen-resultados-title">
              Results: {examen.titulo}
            </h2>
            <div className="examen-resultados-score-card">
              {resultado ? (
                <>
                  <h1 style={{ color: resultado.aprobado ? "#28a745" : "#dc3545", margin: "1rem 0" }}>
                    {resultado.aprobado ? "✓ Passed" : "✗ Not Passed"}
                  </h1>
                  {resultado.modo_porcentaje ? (
                    <>
                      <h3 style={{ margin: "0.5rem 0" }}>
                        Score: <strong>{resultado.puntuacion_escalada}%</strong>
                      </h3>
                      <p style={{ color: "#666", margin: "0.5rem 0" }}>
                        (Minimum to pass: {resultado.puntuacion_minima}%)
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 style={{ margin: "0.5rem 0" }}>
                        Scaled Score: <strong>{resultado.puntuacion_escalada}</strong>
                      </h3>
                      <p style={{ color: "#666", margin: "0.5rem 0" }}>
                        (Minimum passing score: {resultado.puntuacion_minima})
                      </p>
                    </>
                  )}
                  <hr style={{ margin: "1.5rem 0", border: "none", borderTop: "1px solid #e0e0e0" }} />
                  <p style={{ fontSize: "1.1rem", margin: "0.5rem 0" }}>
                    Correct answers: <strong>{resultado.puntuacion_bruta}</strong> of{" "}
                    <strong>{resultado.total_preguntas}</strong>
                  </p>
                </>
              ) : (
                <p>Calculating results...</p>
              )}
            </div>
            {resultado && Object.keys(respuestasCorrectas).length > 0 && (
              <MetricasCategorias
                examen={examen}
                respuestasUsuario={respuestas}
                respuestasCorrectas={respuestasCorrectas}
                preguntasOrden={preguntasOrden}
              />
            )}
            {resultado && Object.keys(respuestasCorrectas).length > 0 && (
              <ResultadosDetallados
                examen={examen}
                respuestasUsuario={respuestas}
                respuestasCorrectas={respuestasCorrectas}
                preguntasOrden={preguntasOrden}
              />
            )}
          </div>
          <div className="resultados-actions">
            <button onClick={onVolver} className="btn-footer btn-primario">
              Back to list
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="examen-layout examen-scroll-layout">
      <header className="examen-main-header">
        <img src="/logos/examcat_logo.png" alt="Examcat" className="brand-logo" />
        <ProfileIcon />
      </header>

      <div className="examen-scroll-main-row">
        <main className="examen-scrollable-content">
          <div className="question-meta-bar">
            <div className="question-pagination-left">
              <strong>{examen.titulo}</strong>
              <span className="examen-scroll-count"> — {totalPreguntas} questions</span>
            </div>
            {modoExamen === "real" && (
              <div className="question-timer-section">
                <div className="timer-toggle-container">
                  <label className="timer-toggle-label">
                    <input
                      type="checkbox"
                      checked={timerVisible}
                      onChange={(e) => setTimerVisible(e.target.checked)}
                      className="timer-toggle-checkbox"
                    />
                    <span className="timer-toggle-slider"></span>
                  </label>
                </div>
                {timerVisible && (
                  <div className="timer-display">
                    <div className="timer-label">TIME REMAINING</div>
                    <div
                      className={`timer-value ${tiempoRestante <= 300 ? "timer-warning" : ""} ${
                        tiempoRestante === 0 ? "timer-expired" : ""
                      }`}
                    >
                      {formatTiempo(tiempoRestante)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="examen-scroll-list">
            <div className="examen-scroll-cards">
              {preguntas.map((pregunta, index) => {
                const showReveal = modoExamen === "training" && !!reveladas[pregunta.id];
                const isCaseStudy = pregunta.tipo_pregunta === "case_study";

                return (
                  <div
                    key={pregunta.id}
                    id={`scroll-q-${index}`}
                    className={`examen-scroll-card ${marcadas[pregunta.id] ? "examen-scroll-card--marked" : ""}`}
                  >
                    <div className="examen-scroll-card-header">
                      <span className="examen-scroll-type">{pregunta.tipo_pregunta}</span>
                      <span className="examen-scroll-badge">Question {index + 1}</span>
                    </div>

                    {isCaseStudy ? (
                      <div className="examen-scroll-case-study">
                        <CaseStudyContent
                          caseStudyData={pregunta.case_study_data}
                          selectedSectionType="question"
                          selectedSectionId={null}
                          pregunta={pregunta}
                          renderPreguntaPrincipal={() => (
                            <PreguntaInteractiva
                              pregunta={pregunta}
                              respuestas={respuestas}
                              respuestasCorrectas={respuestasCorrectas}
                              onSelectOpcion={handleSelectOpcion}
                              onSelectHotspotOpcion={handleSelectHotspotOpcion}
                              onSelectDragDropZone={handleSelectDragDropZone}
                              disabled={isSaving}
                              showCorrectAnswer={showReveal}
                            />
                          )}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="pregunta-texto-container">
                          <h4 className="pregunta-texto">
                            <TextoConImagenes
                              texto={pregunta.texto}
                              imagenes={pregunta.imagenes || []}
                              tablasContenido={pregunta.tablas_contenido || []}
                              basePath="/imagenes/"
                            />
                          </h4>
                        </div>
                        <PreguntaInteractiva
                          pregunta={pregunta}
                          respuestas={respuestas}
                          respuestasCorrectas={respuestasCorrectas}
                          onSelectOpcion={handleSelectOpcion}
                          onSelectHotspotOpcion={handleSelectHotspotOpcion}
                          onSelectDragDropZone={handleSelectDragDropZone}
                          disabled={isSaving}
                          showCorrectAnswer={showReveal}
                        />
                      </>
                    )}

                    <div className="examen-scroll-card-actions">
                      <label className="examen-scroll-mark">
                        <input
                          type="checkbox"
                          checked={!!marcadas[pregunta.id]}
                          onChange={() => toggleMarkReview(pregunta.id)}
                          disabled={isSaving}
                        />
                        Mark to review
                      </label>
                      {modoExamen === "training" && (
                        <button
                          type="button"
                          className="btn-footer btn-check-answer examen-scroll-reveal-btn"
                          onClick={() => toggleReveal(pregunta.id)}
                          disabled={
                            isSaving || (!showReveal && !trainingRevealAvailableFor(pregunta, respuestasCorrectas))
                          }
                        >
                          {showReveal ? "Hide Answer" : "Show Answer"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>

        <aside className="examen-scroll-review-sidebar examen-scroll-review-sidebar--desktop">
          <div className="examen-scroll-review-header">Review</div>
          {renderReviewGrid()}
        </aside>
      </div>

      {mobileReviewOpen && (
        <div className="examen-scroll-review-mobile">
          <div className="examen-scroll-review-mobile-bar">
            <span>Review</span>
            <button
              type="button"
              className="examen-scroll-review-close"
              onClick={() => setMobileReviewOpen(false)}
            >
              Close
            </button>
          </div>
          <div className="examen-scroll-review-mobile-body">
            {renderReviewGrid()}
          </div>
        </div>
      )}

      <div className="examen-scroll-actions">
        <button
          type="button"
          className="btn-footer examen-scroll-review-open-btn"
          onClick={() => setMobileReviewOpen(true)}
        >
          Review
        </button>
        <button
          className="btn-footer btn-secundario"
          onClick={handleSaveAndEndSession}
          disabled={isSaving}
        >
          {isSaving ? "Ending..." : "End Lesson"}
        </button>
        <button className="btn-footer btn-finalizar" onClick={handleEndExam} disabled={isSaving}>
          {isSaving ? "..." : "Finish Exam"}
        </button>
      </div>

      {showConfirmFinish && (
        <div
          className="examen-scroll-confirm-overlay"
          onClick={() => !isSaving && setShowConfirmFinish(false)}
        >
          <div
            className="examen-scroll-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="finish-exam-title"
          >
            <h3 id="finish-exam-title">Finish exam?</h3>
            <p>
              Are you sure you want to finish the exam? Once finished, you will not be able to
              modify it.
            </p>
            <div className="examen-scroll-confirm-actions">
              <button
                type="button"
                className="btn-footer"
                onClick={() => setShowConfirmFinish(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-footer btn-finalizar"
                onClick={() => handleFinalizarExamen(true)}
                disabled={isSaving}
              >
                {isSaving ? "Finishing..." : "Accept"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamenScrollView;
